import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const logo = `${process.env.PUBLIC_URL}/logo-original.png`;

const PRIVACY_CONTACT = 'support@zivugmishamayim.com';
const POLICY_URL = 'https://www.zivugmishamayim.com/privacy';

function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App legal-page">
      <nav className="navbar">
        <div className="container">
          <div className="nav-content">
            <div className="logo">
              <Link to="/" aria-label="Zivug Mishamayim home">
                <img src={logo} alt="Zivug Mishamayim" className="logo-image" />
              </Link>
            </div>
            <div className="nav-links">
              <Link to="/">Home</Link>
              <Link to="/donate">Donate</Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="legal-document">
        <div className="container">
          <article className="legal-document-inner">
            <h1>Privacy Policy</h1>
            <p className="legal-meta">
              <strong>Effective date:</strong> March 25, 2026
              <br />
              <strong>Last updated:</strong> March 25, 2026
            </p>

            <p>
              <strong>Data controller:</strong> Zivug Mishamayim
              <br />
              <strong>Address:</strong> 561 East 3rd Street, Brooklyn, NY 11218, United States
              <br />
              <strong>Contact:</strong>{' '}
              <a href={`mailto:${PRIVACY_CONTACT}`}>{PRIVACY_CONTACT}</a>
            </p>

            <p>
              This Privacy Policy describes how Zivug Mishamayim (“we,” “us”) collects, uses, stores, and
              shares information when you use Zivug Mishamayim (the “App”) and related services (together,
              the “Service”). By using the Service, you agree to this Policy. If you do not agree, do not
              use the Service.
            </p>

            <h2>1. Who this Service is for</h2>
            <p>
              The Service is intended for adults seeking matchmaking within the Jewish community. It is
              not intended for children. We do not knowingly collect personal information from anyone under
              the age of 18. If you believe we have collected information from a minor, contact us and we
              will take appropriate steps to delete it.
            </p>

            <h2>2. Information we collect</h2>
            <h3>Account and authentication</h3>
            <p>
              When you register or sign in, we collect your email address and password (stored using secure
              authentication practices by our providers). We may also collect technical identifiers tied to
              your account (for example a user ID).
            </p>

            <h3>Profile and matchmaking information</h3>
            <p>
              You may provide profile information such as name, photos, demographic and background details,
              religious observance, education, preferences, and other fields you choose to submit. Some
              fields support privacy settings (for example visibility limited to certain users or shared
              only when you respond to a request). Anything you submit may be visible to other users,
              matchmakers, or administrators according to the App’s rules and your settings.
            </p>

            <h3>Photos and files</h3>
            <p>
              Photos or other files you upload are stored and displayed as permitted by your privacy choices
              and the role of the viewer (single, matchmaker, administrator).
            </p>

            <h3>Messages and activity</h3>
            <p>
              We process in-app messages, match or introduction requests, suggestions, responses,
              notifications history, and related activity needed to operate matchmaking workflows.
            </p>

            <h3>Device and technical data</h3>
            <p>
              We may collect device type, operating system, app version, and similar technical data
              necessary to run the App, protect accounts, and diagnose errors.
            </p>

            <h3>Push notifications</h3>
            <p>
              If you grant permission, we collect and store a push notification token associated with your
              account so we can send alerts (for example about suggestions, messages, or status updates).
              You can turn off notifications in your device settings; some features may be less timely as a
              result.
            </p>

            <h3>Local storage</h3>
            <p>The App may store session or preference data on your device (for example to keep you signed in).</p>

            <h2>3. How we use information</h2>
            <p>We use information to:</p>
            <ul>
              <li>Create and manage accounts and authenticate users</li>
              <li>
                Provide profiles, search, filtering, suggestions, introductions, messaging, and related
                matchmaking features
              </li>
              <li>Send service-related communications and push notifications (where enabled)</li>
              <li>
                Enforce our Terms, community standards, and safety (including moderation, fraud prevention,
                and abuse investigation)
              </li>
              <li>Review profiles (including administrator approval workflows)</li>
              <li>Improve reliability and security of the Service</li>
              <li>Comply with legal obligations and respond to lawful requests</li>
            </ul>
            <p>
              As described in our Terms, support or moderation staff may access accounts and content as
              needed for moderation, review, or security.
            </p>
            <p>
              <strong>We do not sell your personal information.</strong>
            </p>

            <h2>4. How we share information</h2>
            <h3>Other users and matchmakers</h3>
            <p>
              Profile information, messages, and activity are shared with other users and matchmakers as
              designed by the Service (for example when you are matched, introduced, or messaged).
            </p>

            <h3>Service providers (processors)</h3>
            <p>
              We use third-party infrastructure providers so the App can function, including:
            </p>
            <ul>
              <li>
                <strong>Supabase</strong> (or similarly described as our database, authentication, storage,
                and server-side processing provider) to host data, authenticate users, store files, and run
                backend logic
              </li>
              <li>
                <strong>Expo</strong> / push delivery infrastructure to register devices and deliver push
                notifications
              </li>
            </ul>
            <p>These providers process data on our behalf under contractual terms and appropriate safeguards.</p>

            <h3>Administrators</h3>
            <p>
              Designated administrators can access user and profile data to operate and moderate the platform.
            </p>

            <h3>Legal and safety</h3>
            <p>
              We may disclose information if required by law, legal process, or to protect the rights,
              safety, or property of users, us, or others.
            </p>

            <h3>Business transfers</h3>
            <p>
              If we are involved in a merger, acquisition, or asset sale, information may be transferred as
              part of that transaction, subject to standard protections.
            </p>

            <h2>5. Where your information is processed</h2>
            <p>Your information is processed in the United States only.</p>

            <h2>6. Retention</h2>
            <p>
              We retain information as long as your account is active and as needed to provide the Service,
              comply with law, resolve disputes, and enforce our agreements. You may request deletion as
              described below; some information may be retained in backups or legal records for a limited
              period where permitted by law.
            </p>

            <h2>7. Your choices and rights</h2>
            <p>
              Depending on your location, you may have rights to access, correct, delete, export, or restrict
              certain processing of your personal information, and to object to certain uses or withdraw
              consent where processing is consent-based.
            </p>
            <p>
              To exercise these rights, contact{' '}
              <a href={`mailto:${PRIVACY_CONTACT}`}>{PRIVACY_CONTACT}</a>. We may need to verify your
              identity before responding.
            </p>
            <p>
              You can update much of your profile data in the App. You can sign out or disable push
              notifications in your device settings.
            </p>

            <h2>8. Security</h2>
            <p>
              We implement reasonable technical and organizational measures designed to protect information.
              No method of transmission or storage is completely secure; we cannot guarantee absolute
              security.
            </p>

            <h2>9. Third-party links and services</h2>
            <p>
              The Service may link to third-party sites or app stores. Their practices are governed by their
              own policies, not this one.
            </p>

            <h2>10. Changes to this Policy</h2>
            <p>
              We may update this Policy from time to time. We will post the updated version at{' '}
              <a href={POLICY_URL} rel="noopener noreferrer">
                {POLICY_URL}
              </a>{' '}
              and update the “Last updated” date. Continued use after changes means you
              accept the updated Policy, except where your consent is required by law.
            </p>

            <h2>11. Contact</h2>
            <p>
              Questions about this Policy:{' '}
              <a href={`mailto:${PRIVACY_CONTACT}`}>{PRIVACY_CONTACT}</a>
            </p>
          </article>
        </div>
      </main>

      <footer className="footer legal-footer">
        <div className="container">
          <div className="footer-bottom">
            <p>
              <Link to="/">← Back to home</Link>
              {' · '}
              <Link to="/support">Support</Link>
              {' · '}
              <a href={`mailto:${PRIVACY_CONTACT}`}>Contact</a>
            </p>
            <p>&copy; 2026 Zivug Mishamayim. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicyPage;
